/** Public estimate approval — unauth, by token. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, FileText } from "lucide-react";

const inr = (m) => `₹${(Math.round((m || 0) / 100)).toLocaleString("en-IN")}`;

export default function PublicEstimateApproval() {
    const { token } = useParams();
    const [est, setEst] = useState(null);
    const [error, setError] = useState(null);
    const [decisions, setDecisions] = useState({});
    const [notes, setNotes] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const root = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");

    useEffect(() => {
        let alive = true;
        fetch(`${root}/api/public/estimates/${token}`)
            .then((r) => r.json())
            .then((d) => {
                if (!alive) return;
                if (d.error) { setError(d.error.message || "Not found"); return; }
                setEst(d);
                // Pre-seed decisions from server-side line state
                const dec = {};
                for (const l of d.lines || []) {
                    if (l.approval_status === "accepted") dec[l.line_id] = "accept";
                    else if (l.approval_status === "rejected") dec[l.line_id] = "reject";
                }
                setDecisions(dec);
            })
            .catch(() => alive && setError("Could not load estimate"));
        return () => { alive = false; };
    }, [root, token]);

    if (error) {
        return (
            <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center p-6"
                  data-testid="public-estimate-error">
                <div className="max-w-md text-center">
                    <div className="text-2xl font-bold text-brand-red">Link expired</div>
                    <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{error}</p>
                </div>
            </div>
        );
    }
    if (!est) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;

    const finalState = est.status === "approved" || est.status === "partially_approved" || est.status === "rejected";
    const allDecided = (est.lines || []).every((l) => decisions[l.line_id]);

    const submit = async () => {
        if (!allDecided) return;
        setSubmitting(true);
        try {
            const r = await fetch(`${root}/api/public/estimates/${token}/respond`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    line_decisions: (est.lines || []).map((l) => ({
                        line_id: l.line_id, accept: decisions[l.line_id] === "accept",
                        note: notes[l.line_id] || null,
                    })),
                }),
            });
            const d = await r.json();
            if (d.error) throw new Error(d.error.message || "Submit failed");
            setEst(d);
            setSubmitted(true);
        } catch (e) {
            setError(String(e));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950" data-testid="public-estimate-page">
            <div className="mx-auto max-w-2xl px-4 py-8">
                <header className="mb-6">
                    <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Estimate review</div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight"
                          data-testid="public-estimate-branch-name">{est.branch?.name}</h1>
                    <p className="mt-1 text-sm text-zinc-500">
                        Estimate <span className="font-mono" data-testid="public-estimate-number">{est.estimate_number}</span>
                        {" · for "}<span data-testid="public-estimate-customer-first-name">{est.customer_first_name}</span>
                    </p>
                </header>

                <div className="space-y-3" data-testid="public-estimate-lines">
                    {(est.lines || []).map((l) => {
                        const d = decisions[l.line_id];
                        return (
                            <div key={l.line_id}
                                  data-testid={`public-estimate-line-${l.line_id}`}
                                  className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-xs font-mono uppercase tracking-widest text-zinc-400">Line {l.line_no} · {l.line_type}</div>
                                        <div className="mt-1 font-medium" data-testid={`public-line-desc-${l.line_id}`}>{l.description}</div>
                                        <div className="mt-1 text-xs text-zinc-500 font-mono">
                                            {l.quantity} {l.unit_of_measure || ""} · {inr(l.unit_price_minor)} · {l.tax_rate_percent}% GST
                                        </div>
                                    </div>
                                    <div className="text-right font-mono" data-testid={`public-line-total-${l.line_id}`}>{inr(l.line_total_minor)}</div>
                                </div>
                                {!submitted && !finalState && (
                                    <div className="mt-3 flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setDecisions({ ...decisions, [l.line_id]: "accept" })}
                                            data-testid={`public-line-accept-${l.line_id}`}
                                            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-mono ${
                                                d === "accept"
                                                    ? "border-emerald-500 bg-emerald-50 text-emerald-800 dark:border-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-200"
                                                    : "border-zinc-200 hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-800/40"
                                            }`}>
                                            <CheckCircle2 className="h-3 w-3" /> Accept
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDecisions({ ...decisions, [l.line_id]: "reject" })}
                                            data-testid={`public-line-reject-${l.line_id}`}
                                            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-mono ${
                                                d === "reject"
                                                    ? "border-brand-red bg-brand-redSoft text-brand-redDeep dark:border-brand-red dark:bg-brand-red/10 dark:text-brand-redSoft"
                                                    : "border-zinc-200 hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-800/40"
                                            }`}>
                                            <XCircle className="h-3 w-3" /> Reject
                                        </button>
                                        {d === "reject" && (
                                            <input
                                                type="text"
                                                value={notes[l.line_id] || ""}
                                                onChange={(e) => setNotes({ ...notes, [l.line_id]: e.target.value })}
                                                data-testid={`public-line-reject-note-${l.line_id}`}
                                                placeholder="Why? (optional)"
                                                className="flex-1 rounded-md border border-zinc-200 px-2 py-1 text-xs dark:border-zinc-800 dark:bg-zinc-900"
                                            />
                                        )}
                                    </div>
                                )}
                                {(finalState || submitted) && (
                                    <div className="mt-3 text-xs font-mono text-zinc-500"
                                          data-testid={`public-line-final-status-${l.line_id}`}>
                                        Status: {l.approval_status}
                                        {l.customer_note ? ` · Note: ${l.customer_note}` : ""}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="mt-6 rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-zinc-500">Subtotal</span>
                        <span className="font-mono">{inr(est.subtotal_minor)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-zinc-500">Tax</span>
                        <span className="font-mono">{inr(est.tax_total_minor)}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between border-t border-zinc-200 pt-2 text-base font-semibold dark:border-zinc-800">
                        <span>Total</span>
                        <span className="font-mono" data-testid="public-estimate-total">{inr(est.total_minor)}</span>
                    </div>
                </div>

                {!submitted && !finalState && (
                    <div className="mt-6">
                        <button
                            type="button"
                            onClick={submit}
                            disabled={!allDecided || submitting}
                            data-testid="public-estimate-submit"
                            className="w-full rounded-xl bg-brand-red px-4 py-3 font-semibold text-white shadow-lg shadow-brand-red/20 transition hover:bg-brand-redDeep disabled:opacity-50">
                            {submitting ? "Submitting…" : (allDecided ? "Submit decisions" : "Decide on every line first")}
                        </button>
                    </div>
                )}
                {(submitted || finalState) && (
                    <div className="mt-6 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-center text-sm dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
                          data-testid="public-estimate-confirmation">
                        <CheckCircle2 className="mx-auto mb-2 h-8 w-8" />
                        <div className="font-semibold">Thanks! Your response was recorded.</div>
                        <div className="mt-1 text-xs">Estimate status: <span data-testid="public-estimate-final-status" className="font-mono">{est.status}</span></div>
                    </div>
                )}
            </div>
        </div>
    );
}
